import * as HexColors from "./hex-colors.js"
import Transaction from "./Transaction.js";
import Filter from "./Filter.js";

/**
 * Functions related to filters.
 * Filter query language is inspired by MongoDB query language.
 *
 * @version 2026-09-03
 * @author Patrik Harag
 */
export default class Filters {

    static QUERY_OPERATORS = ['$eq', '$contains', '$regex', '$gt', '$gte', '$lt', '$lte', '$or', '$and', '$not', '$filter'];
    static QUERY_FIELDS = ['description', 'date', 'value', 'dataset'];

    static DEFAULT = [
        {
            "name": "<no filter>",
            "hideInTable": true,
        },
    ];


    /**
     *
     * @param query
     * @param [resolveFilter] {function(string): Filter|null|undefined}
     * @return {function(Transaction): boolean}
     */
    static compile(query, resolveFilter) {
        return function(transaction) {
            return Filters.#matches(transaction, query, resolveFilter)
        }
    }

    static #matches(transaction, query, resolveFilter) {
        switch (typeof query) {
            case 'number':
            case 'string':
                // field not specified => default field
                return Filters.#matchesField(transaction.getDescription(), query);
            case 'object':
                for (let [key, value] of Object.entries(query)) {
                    switch (key) {
                        // fields
                        case 'description':
                            if (!Filters.#matchesField(transaction.getDescription(), value)) {
                                return false;
                            }
                            break;
                        case 'date':
                            if (!Filters.#matchesField(transaction.getDate(), value)) {
                                return false;
                            }
                            break;
                        case 'value':
                            if (!Filters.#matchesField(transaction.getNativeValue(), value)) {
                                return false;
                            }
                            break;
                        case 'dataset':
                            if (!Filters.#matchesField(transaction.getDataset(), value)) {
                                return false;
                            }
                            break;

                        case '$filter':
                            if (!Filters.#matchesNamedFilter(transaction, value, resolveFilter)) {
                                return false;
                            }
                            break;

                        // top level logical operations
                        case '$or':
                            for (let q of value) {
                                if (Filters.#matches(transaction, q, resolveFilter)) {
                                    return true;
                                }
                            }
                            return false;
                        case '$and':
                            for (let q of value) {
                                if (!Filters.#matches(transaction, q, resolveFilter)) {
                                    return false;
                                }
                            }
                            return true;
                        case '$not':
                            return !Filters.#matches(transaction, value, resolveFilter);

                        default:
                            throw 'Filter: Token not expected: ' + key
                    }
                }
                return true;
            default:
                throw 'Filter: Unsupported field: ' + query
        }
    }

    static #matchesNamedFilter(transaction, name, resolveFilter) {
        if (typeof name !== 'string') {
            throw 'Filter: $filter expects a filter name (string)';
        }
        if (resolveFilter == null) {
            throw 'Filter: $filter cannot be resolved: ' + name;
        }
        let f = resolveFilter(name);
        if (f == null) {
            throw 'Filter: filter not found: ' + name;
        }
        return f.matches(transaction);
    }

    static #matchesField(field, query) {
        switch (typeof query) {
            case 'number':
                return field === query;
            case 'string':
                let normalizedField = field.toLowerCase();
                let normalizedQuery = query.toLowerCase();
                return normalizedField.includes(normalizedQuery);
            case 'object':
                for (let [key, value] of Object.entries(query)) {
                    if (!Filters.#matchesFieldOperator(field, key, value)) {
                        return false;
                    }
                }
                return true;
            default:
                throw 'Filter: Unsupported field: ' + field
        }
    }

    static #matchesFieldOperator(field, operator, value) {
        switch (operator) {
            case '$eq':
                return (field === value)

            // strings
            case '$contains':
                let normalizedField = field.toLowerCase();
                let normalizedQuery = value.toLowerCase();
                return normalizedField.includes(normalizedQuery);
            case '$regex':
                let regex = new RegExp(value, "i");
                return field.match(regex) !== null;

            // numbers
            case '$gt':
                return (field > value)
            case '$gte':
                return (field >= value)
            case '$lt':
                return (field < value);
            case '$lte':
                return (field <= value);

            // logical
            case '$or':
                for (let query of value) {
                    if (Filters.#matchesField(field, query)) {
                        return true;
                    }
                }
                return false;
            case '$and':
                for (let query of value) {
                    if (!Filters.#matchesField(field, query)) {
                        return false;
                    }
                }
                return true;
            case '$not':
                return !Filters.#matchesField(field, value);

            // others
            default:
                throw 'Filter: Unsupported operator: ' + operator
        }
    }

    /**
     *
     * @param json
     * @returns {Map<string, Filter>}
     */
    static load(json) {
        let allFilters = new Map();
        let resolveFilter = (name) => allFilters.get(name);

        let loadedFilters = new Map();
        /** @type {Map<string, string[]>} */
        let refsByName = new Map();
        json.forEach((value) => {
            let color = value.color ?? null;
            if (color != null) {
                color = HexColors.colourNameToHex(color);
                if (!HexColors.isHexColor(color)) {
                    throw 'Color format not supported: ' + color
                }
            }
            let query = value.query;
            if (value.negFilter != null) {
                if (query != null) {
                    throw 'Filter: negFilter cannot be combined with query: ' + value.name;
                }
                query = { '$not': { '$filter': value.negFilter } };
            }
            let refs = [];
            if (query != null) {
                Filters.#collectFilterRefs(query, refs);
            }
            refsByName.set(value.name, refs);
            loadedFilters.set(value.name, new Filter(value.name, {
                color: color,
                hideInTable: value.hideInTable,
                filterFunc: (query != null) ? Filters.compile(query, resolveFilter) : null
            }));
        })

        // generate synthetic (parent) filters
        loadedFilters.forEach((filter, name) => {
            let index = -1;
            do {
                index = name.indexOf('/', index + 1);
                if (index > 0) {
                    let syntheticName = name.substr(0, index);
                    if (!loadedFilters.has(syntheticName) && !allFilters.has(syntheticName)) {
                        let syntheticFilter = Filters.#createSyntheticFilter(syntheticName, loadedFilters);
                        allFilters.set(syntheticName, syntheticFilter);
                        let childRefs = [];
                        loadedFilters.forEach((_, childName) => {
                            if (childName.startsWith(syntheticName + '/')) {
                                childRefs.push(childName);
                            }
                        });
                        refsByName.set(syntheticName, childRefs);
                    }
                }
            } while (index > 0);

            allFilters.set(name, filter);
        });

        Filters.#assertNoFilterCycles(refsByName);

        return allFilters;
    }

    static #collectFilterRefs(query, refs) {
        if (query === null || typeof query !== 'object') {
            return;
        }
        if (Array.isArray(query)) {
            for (let item of query) {
                Filters.#collectFilterRefs(item, refs);
            }
            return;
        }
        for (let [key, value] of Object.entries(query)) {
            if (key === '$filter') {
                if (typeof value === 'string') {
                    refs.push(value);
                }
            } else {
                Filters.#collectFilterRefs(value, refs);
            }
        }
    }

    /**
     *
     * @param refsByName {Map<string, string[]>}
     */
    static #assertNoFilterCycles(refsByName) {
        let visiting = new Set();
        let visited = new Set();

        function visit(name) {
            if (visited.has(name)) {
                return;
            }
            if (visiting.has(name)) {
                throw 'Filter: cyclic filter reference: ' + name;
            }
            visiting.add(name);
            let refs = refsByName.get(name);
            if (refs != null) {
                for (let ref of refs) {
                    visit(ref);
                }
            }
            visiting.delete(name);
            visited.add(name);
        }

        refsByName.forEach((_, name) => {
            visit(name);
        });
    }

    static #createSyntheticFilter(syntheticName, filters) {
        let subFilters = [];
        filters.forEach((filter, name) => {
            if (name.startsWith(syntheticName + '/')) {
                subFilters.push(filter);
            }
        });

        return new Filter(syntheticName, {
            synthetic: true,
            filterFunc: function (transaction) {
                for (const filter of subFilters) {
                    if (filter.matches(transaction)) {
                        return true;
                    }
                }
                return false;
            }
        });
    }

    /**
     *
     * @param searchText {string}
     * @param [namedFilters] {Map<string, Filter>}
     * @return {Filter}
     */
    static createSearchFilter(searchText, namedFilters) {
        searchText = searchText.trim();
        let resolveFilter = namedFilters != null ? ((name) => namedFilters.get(name)) : undefined;
        let searchFilter;
        if (searchText.startsWith('{') && searchText.endsWith('}')) {
            try {
                searchFilter = Filters.compile(JSON.parse(searchText), resolveFilter);
            } catch (e) {
                searchFilter = Filters.compile(searchText, resolveFilter);
            }
        } else {
            searchFilter = Filters.compile(searchText, resolveFilter);
        }

        let filter = new Filter('"' + searchText + '"', { filterFunc: searchFilter });
        this.testFilters([filter]);
        return filter;
    }

    static createYearFilter(year) {
        year = '' + year;  // make sure it is a string

        return new Filter(year, {
            filterFunc: Filters.compile({
                "date": {
                    "$contains": year
                }
            })
        });
    }

    static concat(filter1, filter2) {
        return new Filter(filter1.getName() + ' & ' + filter2.getName(), {
            filterFunc: function (transaction) {
                return filter1.matches(transaction) && filter2.matches(transaction);
            }
        });
    }

    static testFilters(filters) {
        // dynamic check - should throw an exception if there is a problem...
        let testTransaction = new Transaction('', 'xxx', 10, 'test');
        filters.forEach(f => {
            f.matches(testTransaction);
        });
    }

    static groupByFilters(transactions, filters, noFilterGroupName) {
        // pre-populate groups - because of order...
        let filterGroups = new Map();
        const othersFilter = new Filter(noFilterGroupName);
        for (let filter of filters.values()) {
            filterGroups.set(filter.getName(), {
                filter: filter,
                others: false,
                transactions: []
            });
        }
        filterGroups.set(othersFilter.getName(), {
            filter: othersFilter,
            others: true,
            transactions: []
        });

        // fill groups
        for (let transaction of transactions) {
            let matchedFilterName = null;
            for (let filter of filters.values()) {
                if (filter.isHiddenInTable()) {
                    continue;
                }
                if (filter.isSynthetic()) {
                    continue;
                }
                if (filter.hasFilterFunc() && filter.matches(transaction)) {
                    matchedFilterName = filter.getName();
                    break;
                }
            }

            let current = filterGroups.get((matchedFilterName != null) ? matchedFilterName : othersFilter.getName())
            current.transactions.push(transaction);
        }
        return filterGroups;
    }
}
